<template>
  <div class="container mt-5">
    <h4 class="mb-4" style="color:#b03a45;">
      <i class="fas fa-university me-2 text-warning"></i>{{ $t('bankEdit.title') }}
    </h4>

    <!-- Loader -->
    <div v-if="!ready" class="text-center py-5 text-muted">
      {{ $t('bankEdit.loading') }}
    </div>

    <!-- Form -->
    <form v-else @submit.prevent="handleSubmit">
      <div class="row">

        <!-- Bank -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">
              {{ $t('bankEdit.fields.bank') }}
            </label>
            <select v-model="form.bank_id" class="form-select">
              <option disabled value="">{{ $t('bankEdit.select') }}</option>
              <option v-for="b in banks" :key="b.id" :value="b.id">
                {{ b.name }}
              </option>
            </select>
            <small v-if="validationErrors.bank_id" class="text-danger">
              {{ validationErrors.bank_id[0] }}
            </small>
          </div>
        </div>

        <!-- Payment Method -->
        <div class="col-md-6 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">
              {{ $t('bankEdit.fields.paymentMethod') }}
            </label>
            <select v-model="form.payment_method" class="form-select">
              <option value="Bank">Bank</option>
              <option value="Cash">Cash</option>
            </select>
            <small v-if="validationErrors.payment_method" class="text-danger">
              {{ validationErrors.payment_method[0] }}
            </small>
          </div>
        </div>

        <!-- RIB -->
        <div class="col-md-12 mb-4">
          <div class="p-3 bg-white shadow-sm rounded">
            <label class="form-label fw-bold" style="color:#b03a45;">RIB</label>
            <input
              type="text"
              v-model="form.rib"
              class="form-control"
              placeholder="RIB"
            />
            <small v-if="validationErrors.rib" class="text-danger">
              {{ validationErrors.rib[0] }}
            </small>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="d-flex justify-content-end gap-3 mt-3">
        <button type="button" class="btn action-btn" @click="goBack">
          <i class="fas fa-arrow-left"></i>
        </button>

        <button type="submit" class="btn action-btn">
          <i class="fas fa-save"></i>
        </button>

        <button type="button" class="btn action-btn" @click="goNext">
          <i class="fas fa-arrow-right"></i>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from '@/api'
import { useToast } from 'vue-toastification'

const route = useRoute()
const router = useRouter()
const toast = useToast()

const userId = String(route.query.user_id || '')

const ready = ref(false)
const validationErrors = ref({})
const hasBankDetails = ref(false)

const form = reactive({
  user_id: userId,
  bank_id: '',
  payment_method: 'Bank',
  rib: ''
})

const banks = ref([])

/* Charger la liste des banques */
const fetchBanks = async () => {
  try {
    const res = await axios.get('/banks')
    const raw = res.data
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : []
    banks.value = list
  } catch (e) {
    banks.value = []
    toast.error('Échec du chargement des banques')
  }
}

/* Charger les coordonnées bancaires utilisateur */
const fetchBankDetails = async () => {
  if (!userId) return

  try {
    const res = await axios.get(`/employee/bank-details/${userId}`)
    const data = res?.data?.data

    if (data && typeof data === 'object') {
      hasBankDetails.value = true
      form.bank_id = data.bank_id ?? ''
      form.payment_method = data.payment_method ?? 'Bank'
      form.rib = data.rib ?? ''
    } else {
      hasBankDetails.value = false
    }
  } catch (err) {
    if (err.response?.status === 404) {
      hasBankDetails.value = false
      toast.info("Cet utilisateur n'a pas encore de données bancaires. Vous pouvez les ajouter.")
    } else {
      toast.error("Échec du chargement des informations bancaires")
    }
  }
}

/* Enregistrement ou mise à jour */
const handleSubmit = async () => {
  validationErrors.value = {}

  if (!userId) {
    toast.error("L'identifiant utilisateur est manquant")
    return
  }

  const payload = {
    user_id: form.user_id,
    bank_id: form.bank_id || null,
    rib: form.rib || null,
    payment_method: form.payment_method
  }

  try {
    if (hasBankDetails.value) {
      await axios.put(`/employee/bank-details/${userId}`, payload)
      toast.success('Informations bancaires mises à jour avec succès')
    } else {
      await axios.post('/employee/bank-details', payload)
      hasBankDetails.value = true
      toast.success('Informations bancaires ajoutées avec succès')
    }
  } catch (err) {
    if (err.response?.status === 422) {
      validationErrors.value = err.response.data.errors || {}
      const msg = Object.values(validationErrors.value).flat().join(', ')
      toast.error(msg || 'Erreur de validation')
    } else if (err.response?.status === 403) {
      toast.error("Accès refusé : vérifie ton authentification ou tes permissions")
    } else {
      toast.error("Erreur lors de l'enregistrement des informations bancaires")
    }
  }
}

/* Navigation */
const goBack = () => {
  router.push({
    path: '/employee/information/professional/edit',
    query: { user_id: userId }
  })
}

const goNext = () => {
  router.push({
    path: '/employee/information/documents',
    query: { user_id: userId }
  })
}

/* Initialisation */
onMounted(async () => {
  if (!userId) {
    toast.error("L'identifiant utilisateur est manquant dans l'URL")
    return
  }

  await fetchBanks()
  await fetchBankDetails()
  ready.value = true
})
</script>

<style scoped>
.action-btn {
  background-color:#b03a45;
  color:white;
  width:50px;
  height:45px;
  border-radius:12px;
  transition:transform .15s ease, box-shadow .15s ease;
}
.action-btn:hover{
  transform:translateY(-1px);
  box-shadow:0 6px 14px rgba(176,58,69,.25);
}
</style>
